/**
 * Vault-key keyring for Tier B rotation.
 *
 * Maps each vault-key GENERATION to its vault key string, and tracks the
 * current (newest) generation. `securityStore` populates this on unlock from
 * the current wrapped key plus `encryptionKeyHistory` (older wrapped keys the
 * passphrase can still unwrap). Any `EncryptionService` consults it to decrypt
 * data written under an older generation while a rotation is in progress or
 * incomplete.
 *
 * Module-level singleton on purpose: the 9 `EncryptionService` construction
 * sites pass only the current key string, so historical keys reach them through
 * this shared ring rather than a changed constructor signature. An empty ring
 * means "no rotation in play" and every `EncryptionService` falls back to the
 * single key it was constructed with (step-1 behavior).
 *
 * Holds key material in memory only; never persisted. Cleared on lock/logout.
 */

let generationToKey = new Map();
let currentGeneration = null;

/**
 * @param {Object<number|string,string>} entries  generation -> vault key string
 * @param {number} current  the newest generation (what new writes use)
 */
export function setKeyring(entries, current) {
  const next = new Map();
  for (const [gen, key] of Object.entries(entries || {})) {
    if (key) next.set(Number(gen), key);
  }
  generationToKey = next;
  currentGeneration = current ?? null;
}

export function clearKeyring() {
  generationToKey = new Map();
  currentGeneration = null;
}

/** Vault key string for a generation, or null if the ring doesn't hold it. */
export function getVaultKeyForGeneration(gen) {
  return generationToKey.get(Number(gen)) || null;
}

/** The newest generation (what EncryptionService tags new writes with), or null. */
export function getCurrentGeneration() {
  return currentGeneration;
}

export function hasKeyring() {
  return generationToKey.size > 0;
}
